'use client'
import React from 'react'
import type { LinkDef } from '@hanzo/ui/types'
import { Box, cn } from '@hanzo/ui'
import type { LinkDefExtended, ChildMenu } from '../../site-def/main-nav'
import MobileNavMenuAI from './mobile-nav-menu-ai'
import MobileNavMenuItem from './mobile-nav-menu-item'
import AuthWidget from '../auth/widget'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@hanzo/ui/primitives'
import Link from 'next/link'
import { anchor } from '../../util/anchor'

const MobileNav: React.FC<{
  currentAs: string | undefined
  links: LinkDefExtended[]
  className?: string
  commonItemClx?: string | ((def: LinkDef) => string)
  setChatbotOpen: (open: boolean) => void
  setMenuOpen: (open: boolean) => void
  /** No IAM session on this site, so no sign-in widget. */
  noAuth?: boolean
  /** The site's calls to action, at the foot of the drawer. */
  actions?: LinkDef[]
}> = ({
  currentAs,
  links,
  className = '',
  commonItemClx,
  setChatbotOpen,
  setMenuOpen,
  noAuth = false,
  actions = [],
}) => (
    links.length > 0 ? (
      <Box className={cn('flex flex-col h-full', className)}>
        <MobileNavMenuAI setMenuOpen={setMenuOpen} />
        <Box className="flex flex-col flex-1 overflow-auto">
          <Accordion type="single" collapsible className='w-full h-full'>
            {links.map((el, index) => {
              const itemClx = (commonItemClx) ? (typeof commonItemClx === 'string' ? commonItemClx : commonItemClx(el)) : ''
              const variant = el.variant ?? 'link'
              let internalClx = variant === 'link'
                ? ' text-foreground active:text-accent rounded-none'
                : ' min-w-0'

              if (currentAs && currentAs === el.href) {
                internalClx += ' pointer-events-none'
                if (variant === 'link') {
                  internalClx += ' text-accent'
                }
              }

              if (!el.isAIMenu) {
                return (
                  <AccordionItem key={index} value={el.title ? el.title : ""} className='no-underline border-0 px-6'>
                    <AccordionTrigger className={cn(internalClx, itemClx, '')}>
                      <Box className={cn(internalClx, itemClx, 'flex items-center justify-between w-full pl-3 text-base font-normal leading-6')}>
                        {el.title}
                      </Box>
                    </AccordionTrigger>
                    <AccordionContent>
                      {el.childMenu && (
                        <Box className="px-4">
                          {Object.entries(el.childMenu.reduce((acc, child) => {
                            const group = acc[child.groupName ? child.groupName : ""] || []
                            group.push(child)
                            acc[child.groupName ? child.groupName : ""] = group
                            return acc
                          }, {} as { [key: string]: ChildMenu[] })).map(([groupName, children]) => (
                            <div key={groupName}>
                              <Box className="mt-4 mb-4 font-light text-muted-1">{groupName}</Box>
                              {children.map((child, childIndex) => (
                                <Box key={childIndex} className="m-2">
                                  <Box className="flex items-center py-1">
                                    <span>
                                      {child.icon && child.icon}
                                    </span>
                                    <Link href={child.href} {...anchor(child)}>
                                      <Box tag="span" className="text-muted-2 hover:underline ml-5 hover:text-primary">
                                      {child.title}
                                      </Box>
                                    </Link>
                                  </Box>
                                </Box>
                              ))}
                            </div>
                          ))}
                        </Box>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                )
              }
            })}
          </Accordion>
        </Box>
        {actions.length > 0 && (
          <Box className='flex flex-col gap-3 px-6 pb-8'>
            {actions.map((a) => {
              return (
                <Box tag='a' key={a.href} href={a.href} {...anchor(a)}
                  className={cn('inline-flex items-center justify-center h-11 px-6 rounded-full font-medium no-underline',
                    a.variant === 'primary' ? 'bg-white text-black' : 'border border-white/[0.15] text-white')}>
                  {a.title}
                </Box>
              )
            })}
          </Box>
        )}
        {!noAuth && <AuthWidget className='text-2xl z-10' />}
      </Box>
    ) : null
  );

export default MobileNav;